// src/utils/axiosInstance.js
import axios from "axios";

const api = axios.create({
  baseURL: process.env.REACT_APP_API_BASE || "http://localhost:6990",
  withCredentials: true, // Send httpOnly cookies automatically
});

// CSRF double-submit: the API sets a JS-readable `csrfToken` cookie and
// requires it echoed in the X-CSRF-Token header on every state-changing
// request that authenticates via cookie. The cookie is deliberately readable
// here — an attacker on another origin can make the browser SEND it but
// cannot read it, so they cannot build the matching pair.
function readCookie(name) {
  const match = document.cookie.match(new RegExp("(?:^|;\\s*)" + name + "=([^;]*)"));
  return match ? decodeURIComponent(match[1]) : null;
}

api.interceptors.request.use((config) => {
  config.headers = config.headers || {};
  const legacyToken = localStorage.getItem("token");
  if (legacyToken && !config.headers.Authorization) {
    config.headers.Authorization = "Bearer " + legacyToken;
  }
  const workspaceId = localStorage.getItem("activeWorkspaceId");
  if (workspaceId && !config.headers["X-Workspace-Id"]) {
    config.headers["X-Workspace-Id"] = workspaceId;
    config.headers["X-Client-Id"] = workspaceId;
  }
  const method = (config.method || "get").toLowerCase();
  if (!["get", "head", "options"].includes(method)) {
    const csrfToken = readCookie("csrfToken");
    if (csrfToken) config.headers["X-CSRF-Token"] = csrfToken;
  }
  return config;
});

// Auto-refresh access token on 401 (token expired)
let isRefreshing = false;
let refreshQueue = [];

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // 402 — quota exceeded
    if (error.response?.status === 402) {
      const data = error.response.data || {};
      window.dispatchEvent(new CustomEvent("quota-exceeded", { detail: data }));
      return Promise.reject(error);
    }

    // 401 — try to refresh token
    // Skip refresh for: auth endpoints, already retried requests, and when on login page
    const isAuthEndpoint = originalRequest.url?.includes('/auth/');
    const publicPath = window.location.pathname;
    const isPublicAuthPage = [
      '/login',
      '/signup',
      '/verify-otp',
      '/forgot-password',
    ].includes(publicPath) || publicPath.startsWith('/invite/');
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !isAuthEndpoint &&
      !isPublicAuthPage
    ) {
      originalRequest._retry = true;

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          refreshQueue.push({ resolve, reject });
        }).then(() => api(originalRequest));
      }

      isRefreshing = true;
      try {
        await api.post('/api/auth/refresh');
        refreshQueue.forEach(({ resolve }) => resolve());
        refreshQueue = [];
        return api(originalRequest);
      } catch (refreshErr) {
        refreshQueue.forEach(({ reject }) => reject(refreshErr));
        refreshQueue = [];
        // Only log out on actual auth rejection (401/403), not network errors (deploy gaps)
        const status = refreshErr?.response?.status;
        if (status === 401 || status === 403) {
          localStorage.removeItem("role");
          localStorage.removeItem("token");
          if (!isPublicAuthPage) window.location.href = "/login";
        }
        return Promise.reject(refreshErr);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default api;
